import React, { useState } from "react";

// Components //
import BriefcaseIcon from "../../../components/atoms/icons/Briefcase";
import ClockLeftIcon from "../../../components/atoms/icons/ClockLeft";
import MoneyIcon from "../../../components/atoms/icons/Money";
import DocumentIcon from "../../../components/atoms/icons/Document";
import CheckIcon from "../../../components/atoms/icons/CheckIcon";
import Button from "../../atoms/button/button";

// Utils
import { JobDetails } from "../../../interfaces/JobDetails";
import { formatNumberToCurrencyCOP } from "../../../utils/currency";
import {
    shouldDivideText,
    splitRequirementsTextByLine,
} from "../../../utils/text";

// Styles //
import "./JobGeneralInfo.scss";

const MAIN_CONTAINER_CLASSNAME = "m-job-generalinfo";

interface JobGeneralInfoProps {
    jobDetails: JobDetails;
    onClickPostulate?: VoidFunction;
}

type DetailsListType =
    | "location"
    | "company"
    | "area"
    | "salary"
    | "time"
    | "modality"
    | "contractType";

const RenderIconByDetailsType = (type: DetailsListType): React.ReactNode => {
    switch (type) {
        case "location":
            return <i className="icon-map-marker-alt"></i>;
        case "company":
            return <BriefcaseIcon />;
        case "area":
            return <i className="icon-building"></i>;
        case "salary":
            return <MoneyIcon />;
        case "time":
            return <ClockLeftIcon />;
        case "modality":
            return <i className="icon-user"></i>;
        case "contractType":
            return <DocumentIcon />;
        default:
            return null;
    }
};

const MIN_DESCRIPTION_LENGTH = 400;
const MAX_REQUIREMENTS_TO_SHOW = 6;

const JobGeneralInfo = ({
    jobDetails,
    onClickPostulate,
}: JobGeneralInfoProps) => {
    const [showFullDescription, setShowFullDescription] = useState(false);
    const [showFullRequirements, setShowFullRequirements] = useState(false);

    const {
        description,
        requirements,
        area,
        company,
        salary,
        city,
        workMode,
        contractType,
        country,
        state,
        jobType,
    } = jobDetails;

    const textMustDivide = shouldDivideText({
        text: description,
        minCharacters: MIN_DESCRIPTION_LENGTH,
        minBreaksLines: 2,
    });

    const descriptionToShow =
        textMustDivide && !showFullDescription
            ? `${description.substring(0, 400)}...`
            : description;

    const requirementsFormated = splitRequirementsTextByLine(requirements);

    const requirementsMustDivide =
        requirementsFormated?.length > MAX_REQUIREMENTS_TO_SHOW;

    const requirementsToShow =
        requirementsMustDivide && !showFullRequirements
            ? requirementsFormated?.slice(0, MAX_REQUIREMENTS_TO_SHOW)
            : requirementsFormated;

    const generalInfoItems: {
        type: DetailsListType;
        value: string;
    }[] = [
        {
            type: "location",
            value: `${country}, ${state}, ${city}`,
        },
        {
            type: "time",
            value: jobType,
        },
        {
            type: "company",
            value: company,
        },
        {
            type: "modality",
            value: workMode,
        },
        {
            type: "area",
            value: area,
        },
        {
            type: "contractType",
            value: contractType,
        },
        {
            type: "salary",
            value:
                !salary || salary <= 1
                    ? "Confidencial"
                    : formatNumberToCurrencyCOP(salary),
        },
    ];

    return (
        <div className={MAIN_CONTAINER_CLASSNAME}>
            <div className={`${MAIN_CONTAINER_CLASSNAME}__general-info`}>
                <h2 className="subtitle">Información general</h2>
                <ul className="general-info-list">
                    {generalInfoItems.map((item, idx) => (
                        <li
                            key={`general-info-item-${idx}-${item.type}`}
                            className="general-info-list__item"
                        >
                            <div className="icon">
                                {RenderIconByDetailsType(item.type)}
                            </div>
                            <span className="value">{item.value}</span>
                        </li>
                    ))}
                </ul>
            </div>

            <div className={`${MAIN_CONTAINER_CLASSNAME}__button-container`}>
                <Button
                    type={"button"}
                    classname={
                        "a-button__magenta_aceleradora big center btn-apply gtmVacanteClickButtonPostularme"
                    }
                    text={"Postularme"}
                    isDisabled={undefined}
                    onClickAction={onClickPostulate}
                />
            </div>

            <div className={`${MAIN_CONTAINER_CLASSNAME}__description`}>
                <h2 className="subtitle">Acerca de la vacante</h2>
                <p>{descriptionToShow}</p>

                {textMustDivide && !showFullDescription && (
                    <button
                        className="show-more"
                        onClick={() => setShowFullDescription(true)}
                    >
                        Ver más información
                    </button>
                )}
            </div>

            <div className={`${MAIN_CONTAINER_CLASSNAME}__requirements`}>
                <h2 className="subtitle">Requisitos</h2>

                <ul className="requirements-list">
                    {requirementsToShow?.map((requirement, idx) => (
                        <li
                            key={`requirement-${idx}`}
                            className="requirements-list__item"
                        >
                            <CheckIcon />
                            <span>{requirement}</span>
                        </li>
                    ))}
                </ul>

                {requirementsMustDivide && !showFullRequirements && (
                    <button
                        className="show-more"
                        onClick={() => setShowFullRequirements(true)}
                    >
                        Ver más información
                    </button>
                )}
            </div>
        </div>
    );
};

export default JobGeneralInfo;
